import React, {FC, useEffect, useState} from 'react'
import {Menu, Permission, Role, SystemPermission} from './core/_models'
import {Button, Card, Col, Form, Row, Spinner, Tooltip, OverlayTrigger} from 'react-bootstrap'
import {showSwal} from '../../../../ui-kit'
import {useNavigate, useParams} from 'react-router-dom'
import {
  getAllMenus,
  getRoleById,
  submitNewRole,
  updateRole,
  getSystemPermissions,
} from './core/_requests'
import useAxiosPrivate from '../../../libs/useAxiosPrivate'
import {PageTitle} from '../../../../_metronic/layout/core'
import DataTable from '../../../../ui-kit/DataTable'
import {createColumnHelper} from '@tanstack/react-table'
import {useIntl} from 'react-intl'
import {useAbility} from '@casl/react'
import {AbilityContext} from '../../../modules/auth/core/AbilityContext'
import EconDropdown from '../../../../ui-kit/EconDropdown'



const ManageAvailableRolesDetailPage: FC<{subject: string}> = ({subject}) => {
  const ability = useAbility(AbilityContext)
  const axiosPrivate = useAxiosPrivate()
  const intl = useIntl()
  const navigate = useNavigate()
  const {idRole} = useParams()
  const isDetailMode = Boolean(idRole)
  const isAddMode = !idRole
  const [isLoadingButton, setIsLoadingButton] = useState(false)
  const [isFormValid, setIsFormValid] = useState(false)
  const [isLoadingRole, setIsLoadingRole] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [permissions, setPermissions] = useState<Permission[]>([])
  const [customPermissions, setCustomPermissions] = useState<SystemPermission[]>([])
  const columnHelper = createColumnHelper<Permission>()
  const [roleState, setRoleState] = useState<Role>({
    role: '',
    economy: '',
    description: '',
  })

  const columns = [
    columnHelper.accessor('menu', {
      header: 'Menu',
      cell: (info) => {
        const isParent = !info.row.original.parent_id
        return (
          <span
            style={{
              fontWeight: isParent ? 'bold' : 'normal',
              marginLeft: isParent ? '0px' : '20px',
            }}
          >
            {info.getValue() ? intl.formatMessage({id: info.getValue()}) : info.getValue()}
          </span>
        )
      },
      meta: {
        headerClass: 'ps-4 text-start w-100px',
        cellClass: 'text-start w-100px',
      },
    }),
    columnHelper.accessor('all', {
      header: 'All',
      cell: (info) => (
        <Form.Check
          type='checkbox'
          id={`menu.${info.row.original.menu}.all`}
          checked={info.getValue()}
          onChange={(e) => handlePermissionChange(info.row.index, 'all', e.target.checked)}
          disabled={!isEditMode && !isAddMode}
        />
      ),
      meta: {
        headerClass: 'ps-4 text-start w-50px',
        cellClass: 'text-start w-50px',
      },
      enableSorting: false,
    }),
    columnHelper.accessor('create', {
      header: 'Create',
      cell: (info) => (
        <Form.Check
          type='checkbox'
          id={`menu.${info.row.original.menu}.create`}
          checked={info.getValue()}
          onChange={(e) => handlePermissionChange(info.row.index, 'create', e.target.checked)}
          disabled={!isEditMode && !isAddMode}
        />
      ),
      meta: {
        headerClass: 'ps-4 text-start w-50px',
        cellClass: 'text-start w-50px',
      },
      enableSorting: false,
    }),
    columnHelper.accessor('read', {
      header: 'Read',
      cell: (info) => (
        <Form.Check
          type='checkbox'
          id={`menu.${info.row.original.menu}.read`}
          checked={info.getValue()}
          onChange={(e) => handlePermissionChange(info.row.index, 'read', e.target.checked)}
          disabled={!isEditMode && !isAddMode}
        />
      ),
      meta: {
        headerClass: 'ps-4 text-start w-50px',
        cellClass: 'text-start w-50px',
      },
      enableSorting: false,
    }),
    columnHelper.accessor('update', {
      header: 'Update',
      cell: (info) => (
        <Form.Check
          type='checkbox'
          id={`menu.${info.row.original.menu}.update`}
          checked={info.getValue()}
          onChange={(e) => handlePermissionChange(info.row.index, 'update', e.target.checked)}
          disabled={!isEditMode && !isAddMode}
        />
      ),
      meta: {
        headerClass: 'ps-4 text-start w-50px',
        cellClass: 'text-start w-50px',
      },
      enableSorting: false,
    }),
    columnHelper.accessor('delete', {
      header: 'Delete',
      cell: (info) => (
        <Form.Check
          type='checkbox'
          id={`menu.${info.row.original.menu}.delete`}
          checked={info.getValue()}
          onChange={(e) => handlePermissionChange(info.row.index, 'delete', e.target.checked)}
          disabled={!isEditMode && !isAddMode}
        />
      ),
      meta: {
        headerClass: 'ps-4 text-start w-50px',
        cellClass: 'text-start w-50px',
      },
      enableSorting: false,
    }),
  ]

  const fetchData = async () => {
    try {
      setIsLoadingRole(true)
      // 1. Fetch Menus first to establish structure
      const menusRes = await getAllMenus(axiosPrivate)
      const fetchedMenus: Menu[] = menusRes.data.data.menus

      // Create base permissions from menus
      let currentPermissions: Permission[] = fetchedMenus.map((menu) => ({
        id: menu.id,
        menu_id: menu.id, // Ensure menu_id is available
        parent_id: menu.parent_id,
        menu: menu.title,
        all: false,
        create: false,
        read: false,
        update: false,
        delete: false,
      }))

      // find parent menu first
      const sortedPermissions: Permission[] = []
      const roots = currentPermissions.filter((p) => !p.parent_id)
      const addNodeAndChildren = (node: Permission) => {
        sortedPermissions.push(node)

        const children = currentPermissions.filter((p) => p.parent_id == node.id)
        children.forEach(addNodeAndChildren)
      }
      roots.forEach(addNodeAndChildren)
      currentPermissions = sortedPermissions

      // 2. Fetch Role if edit/detail
      let roleCustomPermissions: number[] = []

      // 2. Fetch Role if edit/detail
      if (isEditMode || isDetailMode) {
        const roleRes = await getRoleById(axiosPrivate, {idRole})
        const roleData = roleRes.data
        setRoleState({
          role: roleData.role,
          economy: roleData.economy,
          description: roleData.description,
        })

        if (roleData.custom_permissions) {
          roleCustomPermissions = roleData.custom_permissions
        }

        // Merge permissions
        if (roleData.permissions) {
          const rolePerms = roleData.permissions
          currentPermissions = currentPermissions.map((cp) => {
            // Match by menu_id if available, fallback to menu title for backward compact regarding legacy permissions if any (but backend now sends menu_id)
            const existing = rolePerms.find(
              (rp) => rp.menu_id === cp.menu_id || rp.menu === cp.menu
            )

            if (existing) {
              return {
                ...cp,
                create: existing.create,
                read: existing.read,
                update: existing.update,
                delete: existing.delete,
                all: existing.create && existing.read && existing.update && existing.delete,
              }
            }
            return cp
          })
        }
      }

      setPermissions(currentPermissions)

      // 3. Fetch System Permissions (Custom Capabilities)
      const sysPermRes = await getSystemPermissions(axiosPrivate)
      let sysPerms: SystemPermission[] = sysPermRes.data.data.map((sp: any) => ({
        ...sp,
        isChecked: false,
      }))

      if (isEditMode || isDetailMode) {
        sysPerms = sysPerms.map((sp) => ({
          ...sp,
          isChecked: roleCustomPermissions.includes(sp.id),
        }))
      }
      setCustomPermissions(sysPerms)

      setIsLoadingRole(false)
    } catch (error) {
      console.error(error)
    }
  }

  const validateFormFields = () => {
    const requiredFields = ['role']
    const isValid = requiredFields.every((key) => {
      const val = (roleState as any)[key]
      return val !== undefined && val !== null && String(val).trim() !== ''
    })

    setIsFormValid(isValid)
  }

  const handleCancel = () => {
    showSwal({
      title: 'Are you sure you want to cancel?',
      icon: 'question',
    }).then((result) => {
      if (result.isConfirmed) {
        if (isAddMode) {
          navigate(`/manage-roles`)
        } else {
          window.location.reload()
        }
      }
    })
  }

  const handlePermissionChange = (index: number, field: keyof Permission, value: boolean) => {
    setPermissions((prev) => {
      let newPermissions = [...prev]
      const targetPermission = newPermissions[index]

      // Helper to update a specific permission object
      const updatePermissionObj = (perm: Permission, f: keyof Permission, v: boolean) => {
        const updated = {...perm, [f]: v}
        // If 'all' is toggled, update others
        if (f === 'all') {
          updated.create = v
          updated.read = v
          updated.update = v
          updated.delete = v
        } else {
          // If any specific permission is unchecked, uncheck 'all'
          if (!v) updated.all = false
          // If all specific permissions are checked, check 'all' (optional, but good UX)
          if (updated.create && updated.read && updated.update && updated.delete) {
            updated.all = true
          }
        }
        return updated
      }

      // Update the clicked item
      newPermissions[index] = updatePermissionObj(targetPermission, field, value)

      const updateChildren = (parentId: string | number) => {
        for (let i = 0; i < newPermissions.length; i++) {
          if (String(newPermissions[i].parent_id) === String(parentId)) {
            // Update this child
            newPermissions[i] = updatePermissionObj(newPermissions[i], field, value)
            // Recurse
            updateChildren(newPermissions[i].menu_id)
          }
        }
      }

      const updateParent = (parentId: any) => {
        if (!parentId) return

        // find parent object to update it
        const parentIndex = newPermissions.findIndex((p) => String(p.menu_id) === String(parentId))

        if (parentIndex > -1) {
          const children = newPermissions.filter((p) => String(p.parent_id) === String(parentId))

          // check if every child has the current field
          const allChildrenChecked = children.every((child) => child[field] === true)

          // update the parent if its status needs to change
          if (newPermissions[parentIndex][field] !== allChildrenChecked) {
            newPermissions[parentIndex] = updatePermissionObj(
              newPermissions[parentIndex],
              field,
              allChildrenChecked
            )

            // recurse upward to check the grandparent
            updateParent(newPermissions[parentIndex].parent_id)
          }
        }
      }

      updateChildren(targetPermission.menu_id)

      updateParent(targetPermission.parent_id)

      return newPermissions
    })
  }

  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const {name, value} = e.target
    setRoleState((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleCustomPermissionChange = (id: number, checked: boolean) => {
    setCustomPermissions((prev) => prev.map((p) => (p.id === id ? {...p, isChecked: checked} : p)))
  }

  const handleSubmit = () => {
    const payload = {
      role: roleState.role,
      economy: roleState.economy || null,
      description: roleState.description || null,
      permissions: permissions.map((p) => ({
        menu_id: p.id, // Use ID as menu_id
        create: p.create,
        read: p.read,
        update: p.update,
        delete: p.delete,
      })),
      custom_permissions: customPermissions.filter((p) => p.isChecked).map((p) => p.id),
    }

    showSwal({
      title: `Are you sure you want to ${isAddMode ? 'create' : 'update'} this role?`,
      icon: 'question',
    }).then(
      (result) => {
        if (!result.isConfirmed) return

        setIsLoadingButton(true)

        if (isEditMode) {
          updateRole(axiosPrivate, idRole, payload)
            .then((res) => {
              if (res.status === 200) {
                showSwal('Role updated', undefined, 'success').then(() => {
                  window.location.reload()
                })
              }
            })
            .catch((err) => {
              console.error(err)
              showSwal('Error', 'Failed to update role', 'error')
            })
            .finally(() => {
              setIsLoadingButton(false)
            })
        } else {
          submitNewRole(axiosPrivate, payload)
            .then((res) => {
              if (res.status === 201) {
                showSwal('New role created', undefined, 'success').then(() => {
                  navigate('/manage-roles')
                })
              }
            })
            .catch((err) => {
              console.error(err)
              showSwal('Error', 'Failed to create role.', 'error')
            })
            .finally(() => {
              setIsLoadingButton(false)
            })
        }
      }
    )
  }

  useEffect(() => {
    fetchData()
  }, [isEditMode, isAddMode, isDetailMode])

  useEffect(() => {
    validateFormFields()
  }, [roleState])

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Home',
            path: '/',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
          {
            title: 'Roles',
            path: 'manage-roles',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
        ]}
      >
        {isAddMode ? 'Add' : isEditMode ? 'Edit' : roleState.role}
      </PageTitle>
      <Row className='mb-5'>
        <Col>
          <Card>
            <Card.Header className='card-header-stretch'>
              <Card.Title className='w-100'>
                <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                  <div className='title d-flex align-items-center'>
                    <span className='fa fa-file-alt px-2'></span>
                    <h3 className='text-gray-700'>
                      {isDetailMode ? `Role Detail` : 'Add New Role'}
                    </h3>
                  </div>
                  {isDetailMode && ability.can('update', subject) && !isEditMode ? (
                    <span className='d-inline-block'>
                      <Button
                        className='btn btn-primary d-flex gap-4 align-items-center'
                        onClick={() => setIsEditMode(true)}
                        style={{pointerEvents: isEditMode ? 'none' : 'auto'}} // important
                      >
                        <span className='fa fa-pencil'></span>
                        Edit
                      </Button>
                    </span>
                  ) : (
                    ''
                  )}
                </div>
              </Card.Title>
            </Card.Header>
            <Card.Body>
              {!isLoadingRole ? (
                <>
                  {isAddMode || isEditMode ? (
                    <Form className='form-role-detail'>
                      <div className='row'>
                        <div className='col-12'>
                          <h3>General Information</h3>
                        </div>
                        <div className='col-md-6'>
                          <Form.Label className='text-primary me-0 ps-0' column>
                            <span className='required'>Role Name</span>
                          </Form.Label>
                          <Form.Control
                            placeholder='Enter full role name'
                            type='text'
                            name='role'
                            value={roleState.role}
                            onChange={handleFormChange}
                          />
                        </div>
                        <div className='col-md-6'>
                          <Form.Label className='text-primary me-0 ps-0' column>
                            <span>Economy</span>
                          </Form.Label>
                          <EconDropdown
                            onSelect={(econ) => setRoleState({...roleState, economy: econ})}
                            econ={roleState.economy}
                          />
                        </div>
                        <div className='col-md-12'>
                          <Form.Label className='text-primary me-0 ps-0' column>
                            <span>Description</span>
                          </Form.Label>
                          <textarea
                            className='form-control mb-3'
                            rows={2}
                            data-kt-element='input'
                            placeholder='Role description (optional)'
                            name='description'
                            value={roleState.description}
                            onChange={handleFormChange}
                          ></textarea>
                        </div>
                      </div>
                      <hr />
                      <div className='row mt-5'>
                        <div className='col-12'>
                          <h3>Permissions</h3>
                          <DataTable<Permission>
                            data={permissions}
                            columns={columns}
                            enablePagination={false}
                            enableSorting={true}
                            enableGlobalFilter={true}
                            globalFilterPlaceholder='Search Permissions'
                            className='my-custom-table-wrapper'
                          />
                        </div>
                      </div>
                      <hr />
                      <div className='row mt-5'>
                        <div className='col-12'>
                          <h3>Custom Permissions</h3>
                          <div className='row mt-5'>
                            {customPermissions.map((p) => (
                              <div className='col-md-8 mb-3' key={p.id}>
                                <div className='d-flex align-items-center'>
                                  <Form.Check
                                    type='checkbox'
                                    id={`perm_${p.code}`}
                                    label={`${p.name}`}
                                    checked={p.isChecked}
                                    onChange={(e) =>
                                      handleCustomPermissionChange(p.id, e.target.checked)
                                    }
                                    disabled={!isEditMode && !isAddMode}
                                  />
                                  <OverlayTrigger
                                    placement='top'
                                    overlay={
                                      <Tooltip id={`tooltip_${p.code}`}>{p.description}</Tooltip>
                                    }
                                  >
                                    <i
                                      className='fa fa-question-circle ms-2'
                                      style={{cursor: 'pointer'}}
                                    ></i>
                                  </OverlayTrigger>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className='row pt-5'>
                        <div className='col-lg-12 d-flex justify-content-end gap-3'>
                          <Button variant='default' className='btn-cancel' onClick={handleCancel}>
                            Cancel
                          </Button>
                          <Button
                            variant='primary'
                            onClick={handleSubmit}
                            disabled={isLoadingButton || !isFormValid}
                          >
                            {isLoadingButton ? (
                              <>
                                <Spinner animation='border' size='sm' className='me-2' />
                                Submit
                              </>
                            ) : (
                              'Submit'
                            )}
                          </Button>
                        </div>
                      </div>
                    </Form>
                  ) : (
                    <div>
                      <div className='row'>
                        <div className='col-12'>
                          <h3>General Information</h3>
                        </div>
                        <div className='col-md-6 mb-3'>
                          <Form.Label className='text-primary me-0 ps-0' column>
                            Role Name
                          </Form.Label>
                          <div className='data-value'>{roleState.role}</div>
                        </div>
                        <div className='col-md-6 mb-3'>
                          <Form.Label className='text-primary me-0 ps-0' column>
                            Economy
                          </Form.Label>
                          <div className='data-value'>{roleState.economy}</div>
                        </div>
                        <div className='col-md-12 mb-3'>
                          <Form.Label className='text-primary me-0 ps-0' column>
                            Description
                          </Form.Label>
                          <div className='data-value'>{roleState.description}</div>
                        </div>
                      </div>
                      <div className='row mt-5'>
                        <div className='col-12'>
                          <h3>Permissions</h3>
                          <DataTable<Permission>
                            data={permissions}
                            columns={columns}
                            enablePagination={false}
                            enableSorting={true}
                            enableGlobalFilter={true}
                            globalFilterPlaceholder='Search Permissions'
                            className='my-custom-table-wrapper'
                          />
                        </div>
                      </div>
                      <div className='row mt-5'>
                        <div className='col-12 mt-4'>
                          <h3>Custom Permissions</h3>
                          {customPermissions.map((p) => (
                            <div className='col-md-8 mb-3 mt-5' key={p.id}>
                              <div className='d-flex align-items-center'>
                                <Form.Check
                                  type='checkbox'
                                  id={`perm_view_${p.code}`}
                                  label={p.name}
                                  checked={p.isChecked}
                                  disabled={true}
                                />
                                <OverlayTrigger
                                  placement='top'
                                  overlay={
                                    <Tooltip id={`tooltip_${p.code}`}>{p.description}</Tooltip>
                                  }
                                >
                                  <i
                                    className='fa fa-question-circle ms-2'
                                    style={{cursor: 'pointer'}}
                                  ></i>
                                </OverlayTrigger>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div
                  className='d-flex align-items-center justify-content-center'
                  style={{height: '50vh'}}
                >
                  <Spinner animation='border' role='status' variant='primary' className='me-3' />
                  <div className='loading-text'>
                    Loading
                    <span className='dot'>.</span>
                    <span className='dot'>.</span>
                    <span className='dot'>.</span>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  )
}

export default ManageAvailableRolesDetailPage
